// src/components/EasterEggs/CursorEasterEgg.jsx - Enhanced with rocket to moon Easter egg using Framer Motion
import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { useTheme } from '../../contexts/ThemeContext';
import { motion, AnimatePresence } from 'framer-motion';

// Animations
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(-20px); }
  to { opacity: 1; transform: translateY(0); }
`;

// Notification when cursor changes
const CursorNotification = styled.div`
  position: fixed;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  background: ${({ theme }) => theme.colors.accent};
  color: white;
  padding: 10px 20px;
  border-radius: 30px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2);
  z-index: 9999;
  opacity: ${props => props.$visible ? 1 : 0};
  transition: opacity 0.3s ease;
  pointer-events: none;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 10px;
  animation: ${fadeIn} 0.3s ease-out;
  
  .cursor-icon {
    width: 24px;
    height: 24px;
    background-image: url(${props => props.$iconUrl});
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;
  }
`;

// Rocket animation container
const RocketContainer = styled(motion.div)`
  position: fixed;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  pointer-events: none;
  z-index: 10000;
  overflow: hidden;
`;

// Styled flame components for rocket
const FlameMain = styled(motion.div)`
  position: absolute;
  width: 10px;
  height: 30px;
  background: #FF7B00;
  border-radius: 50% 50% 20% 20%;
  bottom: -30px;
  left: 50%;
  transform: translateX(-50%);
  transform-origin: center bottom;
`;

const FlameOuter = styled(motion.div)`
  position: absolute;
  width: 18px;
  height: 40px;
  background: #FF4500;
  border-radius: 50% 50% 20% 20%;
  bottom: -35px;
  left: 50%;
  transform: translateX(-50%);
  transform-origin: center bottom;
  z-index: -1;
`;

const FlameShadow = styled(motion.div)`
  position: absolute;
  width: 25px;
  height: 15px;
  background: rgba(255, 69, 0, 0.4);
  filter: blur(5px);
  border-radius: 50%;
  bottom: -45px;
  left: 50%;
  transform: translateX(-50%);
  z-index: -2;
`;

// Smoke particle
const SmokeParticle = styled(motion.div)`
  position: absolute;
  background: rgba(200, 200, 200, 0.5);
  border-radius: 50%;
`;

// Custom cursors with hotspot coordinates
const cursors = [
  {
    name: 'Rocket',
    url: '/cursor/rocket.png',
    css: `
      * {
        cursor: url('/cursor/rocket.png') 10 10, auto !important;
      }
    `
  },
  {
    name: 'Dinosaur',
    url: '/cursor/dino.png',
    css: `
      * {
        cursor: url('/cursor/dino.png') 15 15, auto !important;
      }
    `
  },
  {
    name: 'Pizza',
    url: '/cursor/pizza.png',
    css: `
      * {
        cursor: url('/cursor/pizza.png') 12 12, auto !important;
      }
    `
  },
  {
    name: 'Denis',
    url: '/cursor/denis.png',
    css: `
      * {
        cursor: url('/cursor/denis.png') 15 15, auto !important;
      }
    `,
    keyword: 'denis'
  }
];

// Default activation keyword
const DEFAULT_ACTIVATION_KEYWORD = 'cursor';

// Create a set of smoke particles with different sizes and delays
const SmokeParticles = ({ isLaunching }) => {
  // Return an array of smoke particles with random properties
  return [...Array(15)].map((_, i) => {
    const size = Math.random() * 10 + 5;
    const left = Math.random() * 30 - 15;
    const delay = Math.random() * 0.5;
    
    return (
      <SmokeParticle
        key={i}
        initial={{ opacity: 0, y: 0, x: left }}
        animate={isLaunching ? {
          opacity: [0, 0.7, 0],
          y: [0, -60 - (Math.random() * 40)],
          x: [left, left + (Math.random() * 20 - 10)]
        } : { opacity: 0 }}
        transition={{
          duration: 1 + Math.random(),
          delay: delay,
          ease: "easeOut",
          repeat: isLaunching ? Infinity : 0,
          repeatDelay: Math.random() * 0.5
        }}
        style={{
          width: size,
          height: size,
          bottom: -40,
          left: `calc(50% + ${left}px)`,
        }}
      />
    );
  });
};

// Actual rocket component with flames
const Rocket = ({ isLaunching }) => {
  return (
    <motion.div style={{ position: 'relative' }}>
      {/* The rocket SVG */}
      <motion.svg
        width="40"
        height="80"
        viewBox="0 0 40 80"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        initial={{ y: 0 }}
        animate={isLaunching ? {
          y: [-5, 5, -5],
          rotate: [0, 1, -1, 0]
        } : { y: 0 }}
        transition={{
          duration: 0.5,
          repeat: isLaunching ? Infinity : 0,
          repeatType: "reverse"
        }}
      >
        {/* Rocket body */}
        <path
          d="M20 0C20 0 0 20 0 40V60C0 70 10 80 20 80C30 80 40 70 40 60V40C40 20 20 0 20 0Z"
          fill="#E6E6E6"
        />
        {/* Rocket window */}
        <circle cx="20" cy="30" r="8" fill="#383D6E" />
        {/* Red accents */}
        <path
          d="M0 40C0 20 20 0 20 0C20 0 40 20 40 40H0Z"
          fill="#D12C2C"
        />
        {/* Fins */}
        <path
          d="M0 60V50L-10 70L0 60Z"
          fill="#D12C2C"
        />
        <path
          d="M40 60V50L50 70L40 60Z"
          fill="#D12C2C"
        />
      </motion.svg>

      {/* Flames */}
      <FlameMain
        initial={{ scaleY: 0.5, opacity: 0 }}
        animate={isLaunching ? {
          scaleY: [0.5, 1.2, 0.8],
          opacity: [0.5, 0.9, 0.7]
        } : { scaleY: 0, opacity: 0 }}
        transition={{
          duration: 0.2,
          repeat: isLaunching ? Infinity : 0,
          repeatType: "reverse"
        }}
      />
      
      <FlameOuter
        initial={{ scaleY: 0.5, opacity: 0 }}
        animate={isLaunching ? {
          scaleY: [0.5, 1.5, 0.7],
          opacity: [0.3, 0.7, 0.4]
        } : { scaleY: 0, opacity: 0 }}
        transition={{
          duration: 0.3,
          repeat: isLaunching ? Infinity : 0,
          repeatType: "reverse"
        }}
      />
      
      <FlameShadow
        initial={{ opacity: 0, scale: 0.5 }}
        animate={isLaunching ? {
          opacity: [0.2, 0.6, 0.3],
          scale: [0.5, 1.2, 0.7]
        } : { opacity: 0, scale: 0 }}
        transition={{
          duration: 0.4,
          repeat: isLaunching ? Infinity : 0,
          repeatType: "reverse"
        }}
      />
      
      {/* Smoke particles */}
      <SmokeParticles isLaunching={isLaunching} />
    </motion.div>
  );
};

const CursorEasterEgg = () => {
  const [keySequence, setKeySequence] = useState('');
  const [activeCursor, setActiveCursor] = useState(null);
  const [showNotification, setShowNotification] = useState(false);
  
  // Add theme context to check if we're in light mode
  const { isDark } = useTheme();
  
  // Add state for rocket-to-moon Easter egg
  const [showRocket, setShowRocket] = useState(false);
  const [isLaunching, setIsLaunching] = useState(false);
  const [rocketPosition, setRocketPosition] = useState({ x: 0, y: 0 });
  const [targetPosition, setTargetPosition] = useState({ x: 0, y: 0 });
  const [rocketFlightStage, setRocketFlightStage] = useState('idle'); // 'idle', 'launching', 'flying', 'landing'
  const hoverTimer = useRef(null);
  const rocketSoundRef = useRef(null);
  
  // Add style tag directly to the document head instead of using styled-components
  const injectCustomCursorStyle = (cssText) => {
    // Remove any previously added cursor styles
    const existingStyle = document.getElementById('custom-cursor-style');
    if (existingStyle) {
      existingStyle.remove();
    }
    
    if (!cssText) return;
    
    // Create and inject new style tag
    const styleTag = document.createElement('style');
    styleTag.id = 'custom-cursor-style';
    styleTag.textContent = cssText;
    document.head.appendChild(styleTag);
    
    // Log for debugging
    console.log('Custom cursor style applied:', cssText);
  };
  
  // Track key presses to detect the activation keyword
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Only track alphabetic characters (a-z)
      if (/^[a-z]$/i.test(e.key)) {
        setKeySequence(prev => {
          // Add the new key and keep only the last 20 characters
          const newSequence = (prev + e.key.toLowerCase()).slice(-20);
          console.log('Key sequence:', newSequence); // Debug log
          return newSequence;
        });
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);
  
  // Check if activation keyword is in the sequence
  useEffect(() => {
    // Check for special keyword "denis" first
    if (keySequence.includes('denis')) {
      console.log('Special keyword "denis" detected!'); // Debug log
      
      // Find the Denis cursor specifically
      const denisCursor = cursors.find(cursor => cursor.keyword === 'denis');
      
      if (denisCursor) {
        console.log('Activating Denis cursor!'); // Debug log
        setActiveCursor(denisCursor);
        injectCustomCursorStyle(denisCursor.css);
        setShowNotification(true);
        
        // Reset key sequence
        setKeySequence('');
        
        // Hide notification after 3 seconds
        setTimeout(() => {
          setShowNotification(false);
        }, 3000);
        
        // Reset cursor after 30 seconds
        setTimeout(() => {
          console.log('Resetting cursor');
          setActiveCursor(null);
          injectCustomCursorStyle('');
        }, 30000);
      }
    }
    // Check for default keyword
    else if (keySequence.includes(DEFAULT_ACTIVATION_KEYWORD)) {
      console.log('Default activation keyword detected!'); // Debug log
      
      // Get only the regular cursors (without special keywords)
      const regularCursors = cursors.filter(cursor => !cursor.keyword);
      
      // Activate a random cursor from the regular ones
      const randomIndex = Math.floor(Math.random() * regularCursors.length);
      const newCursor = regularCursors[randomIndex];
      console.log('Activating cursor:', newCursor.name); // Debug log
      
      setActiveCursor(newCursor);
      injectCustomCursorStyle(newCursor.css);
      setShowNotification(true);
      
      // Reset key sequence
      setKeySequence('');
      
      // Hide notification after 3 seconds
      setTimeout(() => {
        setShowNotification(false);
      }, 3000);
      
      // Reset cursor after 30 seconds
      setTimeout(() => {
        console.log('Resetting cursor');
        setActiveCursor(null);
        injectCustomCursorStyle('');
      }, 30000);
    }
  }, [keySequence]);
  
  // Rocket-to-Moon Easter Egg - detect when rocket cursor is active and in light mode
  useEffect(() => {
    // Only activate when rocket cursor is active and we're in light mode
    const isRocketCursor = activeCursor?.name === 'Rocket';
    if (!isRocketCursor || isDark) {
      // Clean up if needed
      if (hoverTimer.current) {
        clearTimeout(hoverTimer.current);
        hoverTimer.current = null;
      }
      return;
    }
    
    console.log('Rocket cursor active in light mode - hover on moon for 5 seconds to see magic!');
    
    // Try multiple strategies to find the theme toggle button
    let themeToggleButton = document.querySelector('button[aria-label="Switch to dark mode"]');
    
    // If not found by aria-label, try to find it by its position and content
    if (!themeToggleButton) {
      // Look for a button in NavActions containing a Moon icon
      const navActions = document.querySelector('nav .NavActions') || document.querySelector('nav div:last-child');
      if (navActions) {
        // Try to find button with Moon icon (in light mode)
        const buttons = navActions.querySelectorAll('button');
        for (const button of buttons) {
          if (button.innerHTML.includes('moon') || button.innerHTML.includes('Moon')) {
            themeToggleButton = button;
            break;
          }
        }
        
        // If still not found, just try the first button in the nav actions area
        if (!themeToggleButton && buttons.length > 0) {
          themeToggleButton = buttons[0];
        }
      }
    }
    
    console.log('Looking for moon button:', themeToggleButton ? 'Found' : 'Not found');
    if (!themeToggleButton) {
      console.log('Could not find the theme toggle button - Easter egg will not work');
      return;
    }
    
    // Get button position for rocket target
    const buttonRect = themeToggleButton.getBoundingClientRect();
    setTargetPosition({ 
      x: buttonRect.left + buttonRect.width / 2, 
      y: buttonRect.top + buttonRect.height / 2 
    });
    
    // Set up hover detection with better debugging
    const handleMouseEnter = () => {
      if (hoverTimer.current) return; // Already hovering
      
      console.log('Moon hover started - keep cursor here for 5 seconds!');
      // Add a temporary border to visually confirm hovering is working
      themeToggleButton.style.boxShadow = '0 0 0 2px red';
      
      // Set countdown messages
      let secondsLeft = 5;
      const countdownInterval = setInterval(() => {
        secondsLeft--;
        console.log(`Countdown: ${secondsLeft} seconds remaining...`);
        if (secondsLeft <= 0) clearInterval(countdownInterval);
      }, 1000);
      
      hoverTimer.current = setTimeout(() => {
        console.log('Launching rocket to the moon!');
        // Clear visual indicator
        themeToggleButton.style.boxShadow = '';
        launchRocket();
        clearInterval(countdownInterval);
      }, 5000); // 5 seconds hover threshold
    };
    
    const handleMouseLeave = () => {
      if (hoverTimer.current) {
        console.log('Moon hover interrupted - try again!');
        // Remove visual indicator
        themeToggleButton.style.boxShadow = '';
        clearTimeout(hoverTimer.current);
        hoverTimer.current = null;
      }
    };
    
    // Add a debug click handler to verify we found the right button
    const originalOnClick = themeToggleButton.onclick;
    themeToggleButton.onclick = (e) => {
      console.log('Theme toggle button clicked - this confirms we found the right element');
      if (originalOnClick) originalOnClick.call(themeToggleButton, e);
    };
    
    // Add listeners
    themeToggleButton.addEventListener('mouseenter', handleMouseEnter);
    themeToggleButton.addEventListener('mouseleave', handleMouseLeave);
    
    // Clean up
    return () => {
      themeToggleButton.removeEventListener('mouseenter', handleMouseEnter);
      themeToggleButton.removeEventListener('mouseleave', handleMouseLeave);
      
      if (hoverTimer.current) {
        clearTimeout(hoverTimer.current);
        hoverTimer.current = null;
      }
      
      // Restore original click handler
      if (originalOnClick) themeToggleButton.onclick = originalOnClick;
    };
  }, [activeCursor, isDark]);
  
  // Function to play rocket launch sound
  const playRocketSound = () => {
    try {
      if (!rocketSoundRef.current) {
        rocketSoundRef.current = new Audio('/sounds/rocket-launch.mp3');
        rocketSoundRef.current.volume = 0.5;
      }
      
      // Reset to start and play
      rocketSoundRef.current.currentTime = 0;
      rocketSoundRef.current.play().catch(e => console.log("Audio play error:", e));
    } catch (error) {
      console.log("Could not play rocket sound:", error);
    }
  };
  
  // Function to launch rocket to the moon
  const launchRocket = () => {
    // Reset timer
    hoverTimer.current = null;
    
    console.log('🚀 ROCKET LAUNCH SEQUENCE INITIATED 🚀');
    
    // Play rocket sound
    playRocketSound();
    console.log('Playing rocket sound...');
    
    // Set initial position (bottom center of screen)
    setRocketPosition({
      x: window.innerWidth / 2,
      y: window.innerHeight + 100 // Start below the viewport
    });
    
    // Show rocket and start launch sequence
    setShowRocket(true);
    setRocketFlightStage('launching');
    
    // Start launch sequence
    setTimeout(() => {
      setIsLaunching(true);
      console.log('Rocket engines ignited!');
      
      // After a short delay, start flying
      setTimeout(() => {
        console.log('Rocket is taking off!');
        setRocketFlightStage('flying');
        
        // When flight is almost complete, prepare for landing
        setTimeout(() => {
          console.log('Rocket approaching the moon!');
          setRocketFlightStage('landing');
          
          // Finally hide the rocket and reset
          setTimeout(() => {
            console.log('Rocket mission complete!');
            setShowRocket(false);
            setIsLaunching(false);
            setRocketFlightStage('idle');
          }, 1000);
        }, 1500);
      }, 1500);
    }, 500);
  };
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      injectCustomCursorStyle('');
      if (hoverTimer.current) {
        clearTimeout(hoverTimer.current);
      }
    };
  }, []);
  
  // Preload cursor images for instant activation
  useEffect(() => {
    cursors.forEach(cursor => {
      const img = new Image();
      img.src = cursor.url;
    });
    
    // Preload sound
    const audio = new Audio();
    audio.src = '/sounds/rocket-launch.mp3';
  }, []);
  
  // Calculate rocket motion variants based on flight stage
  const rocketMotionVariants = {
    launching: {
      y: [window.innerHeight + 100, window.innerHeight - 200],
      x: window.innerWidth / 2,
      transition: { duration: 1.5, ease: "easeInOut" }
    },
    flying: {
      y: [window.innerHeight - 200, targetPosition.y],
      x: [window.innerWidth / 2, targetPosition.x],
      transition: { 
        duration: 1.5, 
        ease: "easeOut",
        y: { type: "spring", stiffness: 50, damping: 10 }
      }
    },
    landing: {
      y: targetPosition.y,
      x: targetPosition.x,
      scale: [1, 0.5, 0],
      transition: { 
        duration: 1,
        scale: { duration: 0.5, ease: "easeInOut" }
      }
    }
  };

  return (
    <>
      <CursorNotification 
        $visible={showNotification} 
        $iconUrl={activeCursor?.url}
      >
        <div className="cursor-icon"></div>
        {activeCursor ? `${activeCursor.name} cursor activated for 30 seconds!` : ''}
      </CursorNotification>
      
      {/* Rocket animation with Framer Motion */}
      <AnimatePresence>
        {showRocket && (
          <RocketContainer>
            <motion.div
              initial={{ 
                x: rocketPosition.x,
                y: rocketPosition.y
              }}
              animate={rocketMotionVariants[rocketFlightStage]}
              style={{ 
                position: 'absolute',
                width: 'fit-content',
                height: 'fit-content',
                transformOrigin: 'center center'
              }}
            >
              <Rocket isLaunching={isLaunching} />
            </motion.div>
          </RocketContainer>
        )}
      </AnimatePresence>
    </>
  );
};

export default CursorEasterEgg;